import { FC, useState } from "react";
import { Area, CartesianGrid, ComposedChart, LabelList, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { UiColors } from "../../../config/ui/colors";

import { RangeLineChartProps } from "./RangeLineChart.type";
import { CustomizedYAxisTick } from "../shared/CustomizedYAxisTick/CustomizedYAxisTick";
import "./RangeLineChart.css";

const formatter = new Intl.NumberFormat('nl-BE', {
	style: 'decimal',
	minimumFractionDigits: 0,
	maximumFractionDigits: 1,
});

const RangeLineChart: FC<RangeLineChartProps> = ({
	data = [],
	dataKeys = [],
	height,
	marginLeft = 0,
	marginRight = 0,
	XAxisLabel,
	XAxisProps,
	YAxisLabel,
	YAxisProps,
	labelProps,
	Tooltip: CustomTooltip,
	showToolTip = true,
	chartYAxisLabelPosition = "outside",
	customXTicks,
	xDomain,
	chartSubtitle,
	yAxisLabelClassName = "text-s text-gray-800 font-bold",
}) => {
	const [activeIndex, setActiveIndex] = useState<number | null>(null);

	return (
		<div className="range-line-chart w-full">
			{YAxisLabel && <p className={yAxisLabelClassName}>{YAxisLabel}</p>}
			{chartSubtitle && <p className="text-s text-gray-800">{chartSubtitle}</p>}

			<ResponsiveContainer width="99%" height={height}>
				<ComposedChart
					data={data}
					margin={{ left: marginLeft, top: 30, right: marginRight, bottom: 0 }}
					layout="horizontal"
					onMouseMove={(state) => setActiveIndex(state?.activeTooltipIndex ?? null)}
					onMouseLeave={() => setActiveIndex(null)}
				>
					<CartesianGrid stroke={UiColors.charts.grid} />

					<XAxis
						{...XAxisProps}
						axisLine={{ stroke: UiColors.black, ...(typeof XAxisProps?.axisLine === "object" ? XAxisProps.axisLine : {}) }}
						tickMargin={10}
						// Set interval to 0 to prevent tick labels & grid lines to disappear
						interval={0}
						tick={{ fill: UiColors.black, ...(typeof XAxisProps?.tick === "object" ? XAxisProps.tick : {}) }}
						ticks={customXTicks}
						tickLine={{ stroke: UiColors.black, strokeWidth: 1 }}
						type="number"
						domain={xDomain}
					/>

					<YAxis
						{...YAxisProps}
						axisLine={{ stroke: UiColors.black, strokeWidth: 0, ...(typeof YAxisProps?.axisLine === "object" ? YAxisProps.axisLine : {}) }}
						tickMargin={0}
						{...(chartYAxisLabelPosition === "outside" && {
							tick: (props) => <CustomizedYAxisTick {...props} {...(typeof YAxisProps?.tick === "object" && { ...YAxisProps.tick })} />,
						})}
						{...(chartYAxisLabelPosition === "inline" && {
							tick: false,
						})}
						tickLine={false}
						width={1}
					/>

					{dataKeys.map((line) => (
						<Area
							key={`${line.name} - range`}
							type="linear"
							dataKey={(d) => (d[line.minName] != null && d[line.maxName] != null ? [d[line.minName], d[line.maxName]] : null)}
							stroke="none"
							fill={line.color}
							fillOpacity={0.3}
							activeDot={false}
							legendType="none"
							tooltipType="none"
							isAnimationActive={false}
						/>
					))}

					{dataKeys.map((line) => {
						const lastValid = data.length - 1 - [...data].reverse().findIndex((d) => d[line.name] != null);

						return (
							<Line
								key={`${line.name} - ${line.color}`}
								type="linear"
								dataKey={line.name}
								strokeWidth={2}
								stroke={line.color}
								dot={{ strokeWidth: 2, fill: line.color }}
								isAnimationActive={false}
							>
								{line?.showLabel && (
									<LabelList
										dataKey={line.name}
										content={(props: any) => {
											const isLastValid = props.index === lastValid;
											const isHovered = activeIndex === props.index;
											if (!isLastValid && !isHovered) return null;
											if (props.value === null || props.value === undefined || props.value === 0) return null;
											return (
												<text
													x={isLastValid ? props.x + 8 : props.x + (props.width ?? 0) / 2}
													y={isLastValid ? props.y : (props.y ?? 0) - 12}
													textAnchor={isLastValid ? "start" : "middle"}
													alignmentBaseline={isLastValid ? "middle" : "auto"}
													fill={isLastValid ? line.color : "#888888"}
													fontWeight={isLastValid ? "bold" : "normal"}
													fontSize={labelProps?.fontSize ?? 12}
												>
													{formatter.format(props.value)}
												</text>
											);
										}}
										{...labelProps}
									/>
								)}
								{line?.label && (
									<LabelList
										dataKey={line.name}
										content={(props: any) => {
											const anchor = line.labelAnchor ?? "last";
											const firstValid = data.findIndex((d) => d[line.name] != null);
											const isTarget = anchor === "last" ? props.index === lastValid : props.index === firstValid;
											if (!isTarget) return null;
											const xOffset = anchor === "last" ? 8 : 0;
											const yOffset = anchor === "last" ? 0 : 20;
											return (
												<text x={props.x + xOffset} y={props.y + yOffset} fill={line.color} fontSize={labelProps?.fontSize ?? 16} fontWeight="bold" alignmentBaseline="middle" textAnchor="start">
													{line.label}
												</text>
											);
										}}
									/>
								)}
							</Line>
						);
					})}

					{typeof CustomTooltip !== "undefined" && (
						<Tooltip
							cursor={false}
							wrapperStyle={{ zIndex: 100 }}
							content={(props) => <CustomTooltip {...props} />}
							isAnimationActive={false}
						/>
					)}

					{typeof CustomTooltip === "undefined" && showToolTip ? <Tooltip /> : null}
				</ComposedChart>
			</ResponsiveContainer>

			{XAxisLabel && <p className="mt-3 text-xs text-black text-center">{XAxisLabel}</p>}
		</div>
	);
};

export { RangeLineChart };
